import React, { useCallback, useEffect, useState } from 'react';

import { LoadingType } from 'actions';
import { setDashboardStep } from 'actions/stages';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { getApplicationInfoForDashboardThunk } from 'thunks/getApplicationInfoForDashboard';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { ApplicationStatus } from 'enums/ApplicationStatus';
import Loader from 'components/Loader/index';
import DashboardFormHeader from 'components/DashboardFormHeader';
import { MONETARY_PREFIX } from 'utils/stringUtils';
import { ProgressBarStatus } from 'enums/ProgressBarStatus';
import FormBody from 'components/FormBody';
import NumberInput from 'components/Inputs/NumberInput';
import { Text } from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers/acceptAutomatedOffer.enums';
import OffersTableWrapper from 'pages/Dashboard/steps/AcceptOffer/common/OffersTableWrapper/OffersTableWrapper';
import ConfirmOfferPopup from 'pages/Dashboard/steps/AcceptOffer/AutomatedOffers/ConfirmOfferPopup/ConfirmOfferPopup';
import DeclineOfferThanksPopup from 'pages/Dashboard/steps/AcceptOffer/common/DeclineOfferThanksPopup';
import { OfferDto, offersApi, OffersResponseDto } from 'api/OffersApi';
import styles from 'pages/Dashboard/steps/AcceptOffer/ManualOffers/AcceptMultiTermOffer/AcceptMultiTermOffer.module.scss';

const AcceptMultiTermOffer = () => {
  const dispatch = useAppDispatch();
  const isGlobalLoading = useAppSelector((state) => state.loading[LoadingType.GetData]);
  const [isLoading, setIsLoading] = useState(true);
  const { applicationId, variables, applicationStatus } = useAppSelector((state) => state.application);
  const { borrowerId } = useAppSelector((state) => state.borrower);
  const [loanOffers, setLoanOffers] = useState<OfferDto[]>([]);
  const [currentLoanOffer, setCurrentLoanOffer] = useState<OfferDto | null>(null);
  const [loanAmount] = useState<number>(variables!.loan_amount!);

  const [isDeclineOfferThanksPopupOpen, setIsDeclineOfferThanksPopupOpen] = useState(false);
  const [isConfirmOfferPopupOpen, setIsConfirmOfferPopupOpen] = useState(false);

  const updateLoanOffers = useCallback((loanOffersResponse: OffersResponseDto) => {
    setLoanOffers(loanOffersResponse.loanOffers);
  }, []);

  const handleApplicationRejected = useCallback(async () => {
    await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
    dispatch(setDashboardStep(null));
  }, [dispatch, borrowerId]);

  const getLoanOffers = useCallback(async () => {
    try {
      setIsLoading(true);

      const loanOffersResponse = await offersApi.getMultipleTermOffers(applicationId!);

      if (loanOffersResponse.isApplicationRejected) {
        await handleApplicationRejected();
      } else {
        updateLoanOffers(loanOffersResponse);
      }
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  }, [applicationId, handleApplicationRejected, updateLoanOffers]);

  useEffect(() => {
    if (applicationStatus === ApplicationStatus.Pitched) {
      getLoanOffers();
    }
  }, [applicationStatus, getLoanOffers]);

  const handleDeclineOffer = async () => {
    try {
      setIsLoading(true);
      await offersApi.declineOffer({ applicationId: applicationId!, isWithdrawn: false });

      setIsDeclineOfferThanksPopupOpen(true);
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDeclineOfferThanksPopup = async () => {
    try {
      setIsLoading(true);
      await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      dispatch(setDashboardStep(null));
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseConfirmOfferPopup = async () => {
    setIsConfirmOfferPopupOpen(false);
  };

  const handleAcceptOffer = async () => {
    try {
      setIsLoading(true);

      const result = await offersApi.acceptMultiTermOffer({
        applicationId: applicationId!,
        term: currentLoanOffer!.term,
      });

      if (result.isApplicationRejected) {
        await handleApplicationRejected();

        return;
      }

      if (currentLoanOffer!.offerUrl) {
        window.open(currentLoanOffer!.offerUrl, '_self');
      } else {
        await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
        dispatch(setDashboardStep(null));
      }
    } catch (error: any) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  const selectLoanOffer = (loanOffer: OfferDto) => {
    setCurrentLoanOffer(loanOffer);
    setIsConfirmOfferPopupOpen(true);
  };

  return (
    <>
      {isLoading || isGlobalLoading ? (
        <Loader />
      ) : (
        <div>
          <DashboardFormHeader
            mainMessage="Congratulations! Your Loan Is Prequalified Subject To Review By The Lending Underwriter"
            percent={ProgressBarStatus.Done}
            isDefaultStyles
          />
          <FormBody formBodyClassName={styles.formBody}>
            <div className={styles.loanAmountInputWrapper}>
              <NumberInput
                label={Text.CashToYou}
                prefix={MONETARY_PREFIX}
                placeholder={MONETARY_PREFIX}
                thousandSeparator
                inputClassName={styles.loanAmountInput}
                labelClassName={styles.loanAmountLabel}
                name="loanAmount"
                value={loanAmount}
              />
            </div>

            <div className={styles.tableTitle}>Lowest Payment Offers</div>
            <OffersTableWrapper loanOffers={loanOffers} onAcceptOffer={selectLoanOffer} isDisabled={false} />
            <span className={styles.declineOfferButton} onClick={handleDeclineOffer}>
              {Text.Decline}
            </span>
          </FormBody>
          {isConfirmOfferPopupOpen && (
            <ConfirmOfferPopup
              cashToYou={loanAmount}
              loanOffer={currentLoanOffer!}
              isModalOpen={isConfirmOfferPopupOpen}
              onSubmit={handleAcceptOffer}
              onClose={handleCloseConfirmOfferPopup}
            />
          )}
          <DeclineOfferThanksPopup
            isModalOpen={isDeclineOfferThanksPopupOpen}
            onClose={handleCloseDeclineOfferThanksPopup}
          />
        </div>
      )}
    </>
  );
};

export default AcceptMultiTermOffer;
